import type {
  SessionRefreshContext,
  SessionRefreshResponse,
} from "@medicorepay/shared/lib/api-client";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const SESSION_GENERATION_KEY = "authSessionGeneration";
const REFRESH_RECOVERY_NONCE_KEY = "authRefreshRecoveryNonce";

export interface WebSessionSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  generation: number;
}

function readGeneration(): number {
  const value = Number.parseInt(
    localStorage.getItem(SESSION_GENERATION_KEY) || "0",
    10,
  );
  return Number.isFinite(value) ? value : 0;
}

function bumpGeneration(): number {
  const generation = readGeneration() + 1;
  localStorage.setItem(SESSION_GENERATION_KEY, String(generation));
  return generation;
}

export function getWebAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getWebRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getWebSessionGeneration(): number {
  return readGeneration();
}

export function captureWebSession(): WebSessionSnapshot {
  return {
    accessToken: getWebAccessToken(),
    refreshToken: getWebRefreshToken(),
    generation: getWebSessionGeneration(),
  };
}

export function getWebRefreshRecoveryNonce(): string | null {
  return localStorage.getItem(REFRESH_RECOVERY_NONCE_KEY);
}

export function setWebRefreshRecoveryNonce(nonce: string): void {
  localStorage.setItem(REFRESH_RECOVERY_NONCE_KEY, nonce);
}

export function clearWebRefreshRecoveryNonce(expectedNonce: string): void {
  if (getWebRefreshRecoveryNonce() === expectedNonce) {
    localStorage.removeItem(REFRESH_RECOVERY_NONCE_KEY);
  }
}

function sessionMatches(snapshot: WebSessionSnapshot): boolean {
  return (
    getWebSessionGeneration() === snapshot.generation &&
    getWebAccessToken() === snapshot.accessToken &&
    getWebRefreshToken() === snapshot.refreshToken
  );
}

function writeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function persistWebAuthSession(
  accessToken: string,
  refreshToken: string,
): void {
  bumpGeneration();
  writeTokens(accessToken, refreshToken);
  localStorage.removeItem(REFRESH_RECOVERY_NONCE_KEY);
}

export function commitWebRefreshedSession(
  response: SessionRefreshResponse,
  context: SessionRefreshContext,
): boolean {
  const snapshot: WebSessionSnapshot = {
    accessToken: context.accessToken,
    refreshToken: context.refreshToken,
    generation:
      typeof context.sessionGeneration === "number"
        ? context.sessionGeneration
        : Number(context.sessionGeneration),
  };
  if (!sessionMatches(snapshot)) return false;
  writeTokens(response.accessToken, response.refreshToken);
  return true;
}

export function commitWebSessionReplacement(
  snapshot: WebSessionSnapshot,
  accessToken: string,
  refreshToken: string,
): boolean {
  if (!sessionMatches(snapshot)) return false;
  bumpGeneration();
  writeTokens(accessToken, refreshToken);
  localStorage.removeItem(REFRESH_RECOVERY_NONCE_KEY);
  return true;
}

/** Invalidates pending refresh writes synchronously before any network wait. */
export function invalidateWebAuthSession(): void {
  bumpGeneration();
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_RECOVERY_NONCE_KEY);
}
